import type {
	GetOrderQuoteValueRangesQuery,
	OrderQuoteValueBreakdownFragmentFragment,
} from '@codezo/graphql';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CurrencyText } from '../CurrencyText';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

type ValuePoint = OrderQuoteValueBreakdownFragmentFragment;
type ValueLine = ValuePoint['lines'][number];
type ValueRange = GetOrderQuoteValueRangesQuery['getOrderQuoteValueRanges'][number];

interface QuoteTotalCellProps {
	/** Missing means the quote has nothing priced — unless `failed` says the request never answered. */
	range?: ValueRange;
	loading?: boolean;
	/** The ranges request failed: no figure is known, which is not the same as there being none. */
	failed?: boolean;
}

/**
 * The best case a quote could become: every product at its dearest tier,
 * WITHOUT delivery and samples — net ex-VAT. Delivery and samples are left out
 * of the headline because they say nothing about how big the deal is, and a
 * sample round can outweigh a small order.
 *
 * Both columns assume the client takes EVERY product, which they need not do —
 * any subset can be accepted. So the tooltip lists the products one by one:
 * each line is what that product adds on its own, and they add up to the
 * figure in the cell. Both columns are real combinations priced server-side by
 * the same summary the batch card uses, never sums of independent extremes.
 */
export function QuoteTotalCell({ range, loading, failed }: QuoteTotalCellProps) {
	if (loading) {
		return <span className="inline-block h-4 w-20 animate-pulse rounded bg-surface-fill" />;
	}
	if (failed) {
		return (
			<span
				className="inline-flex items-center gap-1 text-sm text-status-warn"
				title="Totals could not be loaded. Reload the page to try again."
			>
				<AlertTriangle size={12} />
				Unavailable
			</span>
		);
	}
	const low = range?.productsLow;
	const high = range?.productsHigh;
	const batchesConsidered = range?.batchesConsidered ?? 0;
	if (!high) return <span className="text-text-4">—</span>;

	/* One column when there is nothing to choose between — a single priced
	   combination reads as a range of one otherwise. Compared on every figure
	   the tooltip shows, not on total alone: two combinations can share a total
	   and split it differently between products and freight. */
	const sameCombination =
		!low ||
		(low.productsTotal === high.productsTotal &&
			low.deliveryTotal === high.deliveryTotal &&
			low.total === high.total);
	const points: Array<{ label: string; point: ValuePoint }> =
		!sameCombination && low
			? [
					{ label: 'Cheapest tiers', point: low },
					{ label: 'Best case', point: high },
				]
			: [{ label: 'Best case', point: high }];

	/* Lined up by quote line, not by offer: the two ends can come from different
	   batches, which hold different offers for the same products. Best case
	   leads, since that is the batch the cell's figure comes from. */
	const products = new Map<string, string>();
	for (const { point } of [...points].reverse()) {
		for (const line of point.lines) {
			if (!products.has(line.orderQuoteItemId))
				products.set(line.orderQuoteItemId, line.name);
		}
	}
	const lineFor = (point: ValuePoint, itemId: string): ValueLine | undefined =>
		point.lines.find((line) => line.orderQuoteItemId === itemId);

	const totals: Array<{ label: string; pick: (p: ValuePoint) => number; strong?: boolean }> = [
		{ label: 'All products', pick: (p) => p.productsTotal, strong: true },
		{ label: 'Delivery', pick: (p) => p.deliveryTotal },
		{ label: 'Samples', pick: (p) => p.samplesTotal + p.samplesShippingTotal },
		{ label: 'Total', pick: (p) => p.total, strong: true },
	];

	return (
		// Self-contained provider, as on the feasibility badge: Base UI needs one
		// in scope for the popup to open.
		<TooltipProvider delay={200}>
			<Tooltip>
				{/* A span, not the default button — the cell sits inside a clickable row. */}
				<TooltipTrigger render={<span className="cursor-help" tabIndex={0} />}>
					<CurrencyText
						amount={high.productsTotal}
						className="underline decoration-line-2 decoration-dotted underline-offset-4"
					/>
				</TooltipTrigger>
				<TooltipContent
					surface="panel"
					side="left"
					className="block max-w-none px-3.5 py-2.5"
				>
					<table className="text-xs">
						<thead>
							<tr className="text-text-4">
								<th className="pb-1 text-left font-medium">Per product</th>
								{points.map(({ label }) => (
									<th key={label} className="pb-1 pl-5 text-right font-medium">
										{label}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{[...products].map(([itemId, name]) => (
								<tr key={itemId}>
									<td className="max-w-[200px] truncate pr-2 pb-1 text-text-2">
										{name}
									</td>
									{points.map(({ label, point }) => {
										const line = lineFor(point, itemId);
										return (
											<td
												key={label}
												className="pb-1 pl-5 text-right text-text-2"
											>
												{line ? (
													<>
														<span className="mr-2 text-text-4">
															{line.quantity.toLocaleString('en-US')}{' '}
															pcs
														</span>
														<CurrencyText amount={line.productsTotal} />
													</>
												) : (
													<span className="text-text-4">—</span>
												)}
											</td>
										);
									})}
								</tr>
							))}
							{totals.map((row, i) => (
								<tr
									key={row.label}
									className={cn(
										(i === 0 || i === totals.length - 1) &&
											'border-t border-line-1 [&>td]:pt-1'
									)}
								>
									<td
										className={cn(
											'pr-2 pb-1',
											row.strong ? 'font-medium text-text-1' : 'text-text-3'
										)}
									>
										{row.label}
									</td>
									{points.map(({ label, point }) => (
										<td
											key={label}
											className={cn(
												'pb-1 pl-5 text-right',
												row.strong
													? 'font-medium text-text-1'
													: 'text-text-2'
											)}
										>
											<CurrencyText amount={row.pick(point)} />
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
					<div className="mt-1.5 max-w-[360px] text-2xs text-text-4">
						Assumes the client takes every product — they can accept any subset, and
						each line is what that product adds on its own. Net, ex. VAT, across{' '}
						{batchesConsidered === 1
							? '1 offer batch'
							: `${batchesConsidered} offer batches`}
						, drafts included.
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
